"use client";

import React from "react";
import { ArrowRight, Car, CircleCheck, Fuel } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function VehicleGalleryConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <ScrollArea className="w-full whitespace-nowrap pb-8">
        <div className="flex w-max space-x-6 px-4 md:px-6">
          {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="w-[320px] h-[420px] shrink-0 rounded-xl bg-muted/40 animate-pulse" />)}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>;
  }
  if (error || products.length === 0) {
    return <div className="px-4 md:px-6 py-12 text-center">
        <p className="text-muted-foreground">
          No vehicles currently available for preview. Please check back later.
        </p>
      </div>;
  }
  return <ScrollArea className="w-full whitespace-nowrap pb-8">
      <div className="flex w-max space-x-6 px-4 md:px-6">
        {products.map((product, i) => <Card key={product.id} data-index={i} className="w-[320px] shrink-0 overflow-hidden flex flex-col group border-border/50 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="relative aspect-[4/3] bg-muted overflow-hidden">
              <img src={product.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1588353/img/generic_car.png"} alt={product.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              <div className="absolute top-3 right-3">
                <Badge style={{
              backgroundColor: "var(--chart-2)",
              color: "white",
              border: "none"
            }} className="shadow-sm font-semibold tracking-wide">
                  Weekly Rate
                </Badge>
              </div>
            </div>

            <CardContent className="p-5 flex-grow whitespace-normal">
              <h3 className="font-bold text-xl text-foreground line-clamp-1 mb-1">
                {product.title}
              </h3>

              <div className="flex items-baseline gap-1 mt-2 mb-4">
                <span className="text-3xl font-black text-primary">
                  ${product.price}
                </span>
                <span className="text-sm font-medium text-muted-foreground">
                  / week
                </span>
              </div>

              <div className="space-y-2.5">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Fuel className="w-4 h-4 text-muted-foreground/70" />
                  <span>30+ MPG</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CircleCheck className="w-4 h-4" style={{
                color: "var(--chart-2)"
              }} />
                  <span className="font-medium text-foreground/80">
                    Uber/Lyft Ready
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Car className="w-4 h-4 text-muted-foreground/70" />
                  <span>Maintenance Included</span>
                </div>
              </div>
            </CardContent>

            <CardFooter className="p-5 pt-0 mt-auto">
              <AddToCartButton productId={product.id} className="w-full" buttonClassName="bg-primary text-primary-foreground w-full font-semibold shadow-sm" text="Reserve Vehicle" showQuantity={false} />
            </CardFooter>
          </Card>)}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>;
}
export default function WeeklyGigRentalsPreview() {
  const wp_query_params = {
    per_page: 8,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="weekly-gig-rentals-preview" className="relative bg-background py-16 md:py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05]" style={{
      backgroundImage: `repeating-linear-gradient(-45deg, var(--foreground) 0, var(--foreground) 1px, transparent 1px, transparent 32px)`
    }} />

      <div className="container mx-auto px-4 md:px-6 relative z-10 mb-10 md:mb-14">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Badge variant="outline" className="mb-4 border-primary/20 text-primary bg-primary/5">
              Gig Economy Fleet
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
              Top Vehicles for Gig Drivers
            </h2>
            <p className="text-lg text-muted-foreground">
              Drive more, earn more. Our weekly rentals are fully maintained,
              insured, and ready for Uber, Lyft, and delivery apps.
            </p>
          </div>
          <div className="shrink-0">
            <Button variant="outline" size="lg" className="w-full md:w-auto group" asChild>
              <Link to="/RentACarPage">
                View Full Fleet
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="relative z-10 w-full max-w-[100vw]">
        <EcommerceProductsProvider wp_query={{
        per_page: 8,
        order: "desc",
        orderby: "date"
      }}>
          <VehicleGalleryConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}